/* pages/ReviewQueue.jsx — Sprint 3 Supervisor Authorization Review Queue.
 *
 * Surfaces every timesheet_segment currently in documentation_status =
 * "needs_authorization_review" so a supervisor can attach an existing
 * Service Authorization or (once we ship the inline auth editor) create
 * a new one.
 *
 * Per ADR-0012 this is a projection view — writes flow through the owning
 * modules (individuals.service_authorizations[] and PATCH /timesheet-segments/{id}).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle, CheckCircle2, X, Clock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function ReviewQueue() {
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);   // segment being resolved

  const load = () => {
    setLoading(true);
    api.get("/supervisor/authorization-review-queue")
      .then((r) => setQueue(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <div>
        <div className="accp-label text-xs">Sprint 3 · Supervisor</div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          Authorization Review Queue
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Segments below were documented against a participant who has no matching
          active Service Authorization. Attach one to complete the record. Care
          delivery was <em>not</em> blocked — these are documentation-review items,
          not billing blockers.
        </p>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading queue…</div>}

      {!loading && queue && queue.count === 0 && (
        <div className="p-8 rounded-lg border border-border bg-green-50 flex items-center gap-4"
          data-testid="review-queue-empty">
          <CheckCircle2 size={28} className="text-green-700" />
          <div>
            <div className="font-semibold text-green-900">Queue is clear</div>
            <div className="text-sm text-green-800">
              No segments currently require authorization review. New reviews will
              appear here automatically when a DSP documents service against a
              participant without a matching active authorization.
            </div>
          </div>
        </div>
      )}

      {!loading && queue && queue.count > 0 && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {queue.count} segment{queue.count === 1 ? "" : "s"} awaiting review.
          </div>
          {queue.items.map((it) => (
            <QueueRow key={it.segment_id} item={it}
              onResolve={() => setResolving(it)}
              data-testid={`queue-row-${it.segment_id}`}
            />
          ))}
        </div>
      )}

      {resolving && (
        <ResolveModal item={resolving} onClose={() => setResolving(null)} onSaved={() => { setResolving(null); load(); }} />
      )}
    </div>
  );
}

function QueueRow({ item, onResolve }) {
  return (
    <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50 flex items-center gap-4"
      data-testid={`queue-row-${item.segment_id}`}>
      <AlertTriangle size={20} className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-semibold text-primary">
            {item.individual.name}
          </div>
          <span className="font-mono text-xs text-muted-foreground">{item.individual.code}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-white ring-1 ring-slate-200">
            {item.service_type}
          </span>
          {item.individual.active_authorization_count === 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 ring-1 ring-red-200">
              No active authorizations on file
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Clock size={11} />{new Date(item.start_time).toLocaleString()}</span>
          <span>· {item.timesheet_code}</span>
          {item.dsp?.name && (
            <span className="flex items-center gap-1"><UserIcon size={11} />{item.dsp.name}</span>
          )}
        </div>
      </div>
      <button onClick={onResolve} data-testid={`resolve-btn-${item.segment_id}`}
        className="accp-btn-primary">Resolve</button>
    </div>
  );
}

function ResolveModal({ item, onClose, onSaved }) {
  const [auths, setAuths] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/individuals/${item.individual.id}`)
      .then((r) => {
        const active = (r.data.service_authorizations || []).filter(
          (sa) => !sa.effective_end || sa.effective_end >= item.start_time.slice(0, 10)
        );
        setAuths(active);
        if (active.length === 1) setSelected(active[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [item.individual.id, item.start_time]);

  const save = async () => {
    if (!selected) {
      toast.error("Select an authorization first.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/timesheet-segments/${item.segment_id}`, {
        service_authorization_id: selected,
        service_type: item.service_type,
      });
      toast.success("Segment resolved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="resolve-modal">
      <div className="accp-card w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            Resolve segment
          </h2>
          <button onClick={onClose} data-testid="resolve-close-btn"><X size={20} /></button>
        </div>
        <div className="text-sm space-y-1">
          <div><span className="accp-label">Participant:</span> {item.individual.name} <span className="font-mono text-xs">{item.individual.code}</span></div>
          <div><span className="accp-label">Service:</span> {item.service_type}</div>
          <div><span className="accp-label">When:</span> {new Date(item.start_time).toLocaleString()}</div>
        </div>

        {loading && <div className="text-sm text-muted-foreground">Loading authorizations…</div>}

        {!loading && auths.length === 0 && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">
            This participant has no active Service Authorizations. Add one from the
            participant&apos;s workspace (Individuals → open participant → Authorizations)
            then return here to attach it.
          </div>
        )}

        {!loading && auths.length > 0 && (
          <label className="block">
            <span className="accp-label">Attach authorization</span>
            <select className="accp-input mt-1" value={selected}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="resolve-auth-select">
              <option value="">Select…</option>
              {auths.map((sa) => (
                <option key={sa.id} value={sa.id}>
                  {sa.waiver_code} · {sa.funding_source_code} — {(sa.service_types || []).join(", ")}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="accp-btn-outline" disabled={saving}>Cancel</button>
          <button onClick={save} className="accp-btn-primary"
            disabled={saving || !selected}
            data-testid="resolve-save-btn">
            {saving ? "Saving…" : "Attach & resolve"}
          </button>
        </div>
      </div>
    </div>
  );
}
